import {Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {AffiliateFieldsFragment, useAffiliatesQuery} from 'api/generated';
import {Ellipsize, TableCard} from 'components';
import deepEqual from 'deep-equal';
import {formatDate, formatEmail} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import React, {useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {FilterQueryParams} from 'routes/Accounts/AccountFilters';
import {StringParam, useQueryParams} from 'use-query-params';
import {FilterList} from '../../components/FilterList';
import {AFFILIATE_FILTERS} from '../../lib/enums';
import {sortObjectKeysByValue, truncate} from '../../lib/utils';
import {AffiliateFilters} from './AffiliatesFilters';
import {EmailFavicon} from 'components/EmailFavicon';

export const queryFiltersSchema = {
  email: StringParam
};

export const PROCESSING_AFFILIATE_GROUP_ID = 'ag_CDPoDVSEX8rQhQO-X4';

const buildFilter = (activeTab: string, page: number, values: FilterQueryParams) => {
  const filter = {
    nextToken: page.toString(),
    affiliateGroupId: activeTab === 'active' ? PROCESSING_AFFILIATE_GROUP_ID : ''
  };

  for (const [key, value] of Object.entries(values)) {
    if (value) filter[key] = value;
  }
  return filter;
};

export const formatAffiliateTier = (tier: string) => {
  switch (tier) {
    case '1':
      return 'Platinum';
    default:
      return 'Gold';
  }
};

export const Affiliates: React.FC<RouteComponentProps> = ({history}) => {
  const [queryFilters, setQueryFilters] = useQueryParams(queryFiltersSchema);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('active');

  const {data, loading, refetch, previousData} = useAffiliatesQuery({
    variables: {input: buildFilter(activeTab, page, queryFilters)}
  });

  const payload = data ?? previousData;
  const affiliates = payload?.affiliates.items || [];

  // Extract last page number from links
  const links = payload?.affiliates.links || '';
  const lastPageMatch = links.match(/page=(\d+)>; rel="last"/);
  const lastPage = lastPageMatch ? parseInt(lastPageMatch[1]) : 1;
  const total = lastPage * 25; // Assuming 25 items per page

  const columns: Array<ColumnProps<AffiliateFieldsFragment>> = [
    {
      key: 'favicon',
      dataIndex: ['business', 'website'],
      className: 'minimized-cell',
      onCell: (affiliate) => ({
        onClick() {
          history.push({
            pathname: `/affiliates/${affiliate.id}`
          });
        }
      }),
      render(_email: string, affiliate: AffiliateFieldsFragment) {
        return <EmailFavicon email={affiliate.email} shape="square" />;
      }
    },
    {
      title: 'Company',
      dataIndex: 'company',
      className: 'minimized-cell',
      onCell: (affiliate) => ({
        onClick() {
          history.push({
            pathname: `/affiliates/${affiliate.id}`
          });
        }
      }),
      render(_name: string, item: AffiliateFieldsFragment) {
        return <>{truncate(item.company?.name || '', 35)}</>;
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      className: 'minimized-cell',
      render: (_company: any, item: AffiliateFieldsFragment) => (
        <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>
          {item.firstname + ' ' + item.lastname}
        </Ellipsize>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      className: 'minimized-cell',
      render: (email) => (
        <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>{formatEmail(email)}</Ellipsize>
      )
    },
    {
      title: 'Tier',
      dataIndex: 'meta_data',
      className: 'minimized-cell',
      render: (meta_data: any) => formatAffiliateTier(meta_data.tier)
    },
    {
      title: 'Registration date',
      dataIndex: 'created_at',
      className: 'minimized-cell',
      defaultSortOrder: 'descend',
      render(_, affiliate: AffiliateFieldsFragment) {
        const date = affiliate.created_at;
        if (!date) return null;
        return formatDate(date);
      }
    }
  ];

  const handleFiltersChange = (values: FilterQueryParams) => {
    setActiveTab('all');
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) return refetch();
    setQueryFilters(values, 'push');
  };

  const handleCloseTag = (key: string) => setQueryFilters({...queryFilters, [key]: null}, 'push');

  let availableFilters: Record<string, string> = {...AFFILIATE_FILTERS};

  availableFilters = sortObjectKeysByValue(availableFilters);

  return (
    <>
      <AffiliateFilters
        onChange={handleFiltersChange}
        queryFilters={queryFilters}
        availableFilters={availableFilters}
      />
      <FilterList queryFilters={queryFilters} filters={availableFilters} onClose={handleCloseTag} />
      <TableCard
        size="small"
        tabList={[
          {key: 'active', tab: 'Active'},
          {key: 'all', tab: 'All'}
        ]}
        onTabChange={(value) => {
          setActiveTab(value);
          setPage(1);
        }}
        activeTabKey={activeTab}>
        <Table
          scroll={{x: true}}
          dataSource={affiliates}
          columns={columns}
          loading={loading}
          rowClassName={() => 'clickable'}
          rowKey="id"
          pagination={{
            pageSize: 25,
            current: page,
            total: total,
            showSizeChanger: false,
            onChange: (page) => setPage(page)
          }}
        />
      </TableCard>
    </>
  );
};
